import React, { useState, useEffect } from "react";

import Decimal from "decimal.js";
import { conformToMask } from "react-text-mask";
import { createNumberMask } from "text-mask-addons";

import { TextField } from "src/components/ui/text-field";
import { CURRENCY_SYMBOL_BY_CODE } from "src/constants";
import { cn } from "src/lib/utils";

export const currencyMask = createNumberMask({
  prefix: "",
  suffix: "",
  includeThousandsSeparator: true,
  thousandsSeparatorSymbol: ",",
  allowDecimal: true,
  decimalSymbol: ".",
  decimalLimit: 2,
  integerLimit: null,
  allowNegative: true,
  allowLeadingZeroes: true,
});

export const percentageMask = createNumberMask({
  prefix: "",
  suffix: "",
  includeThousandsSeparator: true,
  thousandsSeparatorSymbol: ",",
  allowDecimal: true,
  decimalSymbol: ".",
  decimalLimit: 6,
  integerLimit: null,
  allowNegative: true,
  allowLeadingZeroes: true,
});

export const decimalMask = createNumberMask({
  prefix: "",
  suffix: "",
  includeThousandsSeparator: true,
  thousandsSeparatorSymbol: ",",
  allowDecimal: true,
  decimalSymbol: ".",
  decimalLimit: 8,
  integerLimit: null,
  allowNegative: true,
  allowLeadingZeroes: true,
});

export const integerMask = createNumberMask({
  prefix: "",
  suffix: "",
  includeThousandsSeparator: true,
  thousandsSeparatorSymbol: ",",
  allowDecimal: false,
  integerLimit: null,
  allowNegative: true,
  allowLeadingZeroes: true,
});

export const unmaskNumeric = (maskedValue: string) =>
  maskedValue.replace(/[^\d.-]+/g, "");

type MaskType = "currency" | "percentage" | "integer" | "decimal";

export type MaskTextFieldProps = React.InputHTMLAttributes<HTMLInputElement> & {
  mask?: MaskType;
  label?: string;
  suffix?: React.ReactNode;
  helperText?: string;
  error?: boolean;
  errorText?: string;
  currency?: string;
  multiline?: boolean;
  rows?: number;
  fullWidth?: boolean;
};

const MaskedTextField = ({
  mask,
  label,
  suffix,
  helperText,
  error,
  className,
  required,
  errorText,
  currency,
  multiline,
  rows,
  ...props
}: MaskTextFieldProps): React.ReactNode => {
  const formatValue = (val: unknown) => {
    if (val == null || val === "") return "";
    const stringVal = val.toString();
    return mask === "percentage"
      ? new Decimal(stringVal).mul(100).toString()
      : stringVal;
  };
  const [inputValue, setInputValue] = useState(formatValue(props.value));

  useEffect(() => {
    setInputValue(formatValue(props.value));
  }, [props.value, mask]);

  let maskValue;
  let unmaskValue: (maskedValue: string) => string;
  let startAdornment: React.ReactNode = null;
  let endAdornment: React.ReactNode = null;

  if (mask === "currency") {
    maskValue = currencyMask;
    unmaskValue = unmaskNumeric;
    startAdornment = (
      <span className="text-muted-foreground mr-1 flex items-center">
        {currency ? CURRENCY_SYMBOL_BY_CODE[currency] : "$"}
      </span>
    );
  } else if (mask === "percentage") {
    maskValue = percentageMask;
    unmaskValue = unmaskNumeric;
    endAdornment = (
      <span className="text-muted-foreground ml-1 flex items-center">%</span>
    );
  } else if (mask === "integer") {
    maskValue = integerMask;
    unmaskValue = unmaskNumeric;
  } else if (mask === "decimal") {
    maskValue = decimalMask;
    unmaskValue = unmaskNumeric;
  }

  let value =
    maskValue != null
      ? conformToMask(String(inputValue), maskValue).conformedValue
      : props.value;

  // HACK - for some reason masking an empty string results in an underscore
  if (value === "_") {
    value = "";
  } else if (value === "-_") {
    value = "-";
  }

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    let unmaskedValue = unmaskValue
      ? unmaskValue(e.target.value)
      : e.target.value;

    setInputValue(e.target.value);

    if (unmaskedValue && mask === "percentage") {
      unmaskedValue = new Decimal(unmaskedValue).div(100).toString();
    }

    e.target.value = unmaskedValue;
    props.onChange?.(e as React.ChangeEvent<HTMLInputElement>);
  };

  return (
    <TextField
      data-1p-ignore
      label={label}
      helperText={helperText}
      error={error}
      errorText={errorText}
      containerClassName={className}
      required={required}
      startAdornment={startAdornment}
      endAdornment={endAdornment}
      multiline={multiline}
      rows={rows}
      className={cn(mask && "text-right")}
      size="small"
      {...props}
      value={value}
      onChange={handleChange}
    />
  );
};

export default MaskedTextField;
